import axios from "axios";
import { Action } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";
import {
    SUBMIT_QUESTIONNAIRE,
    submitQuestionnaireSuccess,
    QuestionnaireAction,
    questionnaireLoaded,
    saveQuestionnaireSuccess
} from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { QUESTIONNAIRE_KEY as BBV_CONSULTATION_QUESTIONNAIRE_KEY } from "@teamprevent/common/bbvConsultation/components/bbvConsultationQuestionnaire";

import { ApplicationState } from "../applicationState";
import { TasksUpdatedSuccessResult, AddTaskModel } from "../task/model";

import { SUBMIT_BBV_CONSULTATION_SUCCESS, LOAD_BBV_CONSULTATION, SAVE_BBV_CONSULTATION } from "./actionTypes";
import { ClinicalNotesApi } from "./../clinicalNotes/ClinicalNotesApi";

export type BbvConsultationAction = { type: typeof SUBMIT_BBV_CONSULTATION_SUCCESS } & Payload<TasksUpdatedSuccessResult>;

export const submitBbvConsultation = buildSubmitBbvConsultation(new ClinicalNotesApi(axios));

export function buildSubmitBbvConsultation(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], tasks: AddTaskModel[]):
        ThunkAction<Promise<void>, ApplicationState, undefined, BbvConsultationAction> => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, BbvConsultationAction | QuestionnaireAction>) => {
            const result = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                clinicalNotesApi.submitBbvConsultation(ohCaseId, taskId, questions, tasks));
            toast.success("Blood borne virus consultation submitted successfully");
            dispatch(submitBbvConsultationSuccess({
                ohCaseId,
                tasks: result
            }));
            dispatch(submitQuestionnaireSuccess(BBV_CONSULTATION_QUESTIONNAIRE_KEY));
        };
    };
}

export const saveBbvConsultation = buildSaveBbvConsultation(new ClinicalNotesApi(axios));

export function buildSaveBbvConsultation(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, SAVE_BBV_CONSULTATION, clinicalNotesApi.saveBbvConsultation(ohCaseId, taskId, questions, eTag));
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: BBV_CONSULTATION_QUESTIONNAIRE_KEY, eTag: response.eTag }));
        };
    };
}

export const loadBbvConsultation = buildLoadBbvConsultation(new ClinicalNotesApi(axios));

export function buildLoadBbvConsultation(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string):
        ThunkAction<Promise<void>, ApplicationState, undefined, BbvConsultationAction> => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, BbvConsultationAction | QuestionnaireAction>) => {
            const response = await loadAndTrack(dispatch, LOAD_BBV_CONSULTATION, clinicalNotesApi.load(ohCaseId, taskId));
            if (response) {
                const questions = response.data.questions || [];
                dispatch(questionnaireLoaded({ questions, questionnaireKey: BBV_CONSULTATION_QUESTIONNAIRE_KEY, eTag: response.eTag }));
            }
        };
    };
}

export function submitBbvConsultationSuccess(payload: TasksUpdatedSuccessResult): BbvConsultationAction {
    return {
        type: SUBMIT_BBV_CONSULTATION_SUCCESS,
        payload
    };
}
